import OutstandingCard from "components/OutstandingCard";
import RevenueCard, { RevenuePeriod, RevenuePeriods } from "components/RevenueCard";
import { OfficeTranslationKey } from "i18n/officeTranslations";
import { DashboardWidgetInstance, DashboardWidgetPosition, DashboardWidgetType } from "models/Dashboard";
import UnreadEmailCard from "pages/Email/UnreadEmailCard";
import React from "react";
import ActiveProjectsOverview from "./ActiveProjectsOverview";
import AssetServicesCard from "./AssetServicesCard";
import OpenInvoicesCard from "./OpenInvoicesCard";
import OrphanedPaymentsCard from "./OrphanedPaymentsCard";
import PendingDocumentsCard from "./PendingDocumentsCard";
import PendingHoursCard from "./PendingHoursCard";
import RecentCallsCard from "./RecentCallsCard";
import SalesAnalytics from "./SalesAnalytics";
import UpcomingAssetServicesCard from "./UpcomingAssetServicesCard";
import WorkerClientBalanceCard from "./WorkerClientBalanceCard";
import { DashboardColumnCount, DashboardWidgetConstraint } from "./dashboardLayout";

export interface DashboardWidgetSettingDefinition {
    key: string;
    labelKey: OfficeTranslationKey;
    minimum: number;
    maximum: number;
    defaultValue: number;
    options?: Array<{
        value: number;
        labelKey: OfficeTranslationKey;
    }>;
}

export interface DashboardWidgetDefinition extends DashboardWidgetConstraint {
    type: DashboardWidgetType;
    titleKey: OfficeTranslationKey;
    descriptionKey: OfficeTranslationKey;
    icon: string;
    defaultPosition: Pick<DashboardWidgetPosition, "width" | "height">;
    settings: DashboardWidgetSettingDefinition[];
    render: (widget: DashboardWidgetInstance) => React.ReactNode;
}

const numberSetting = (widget: DashboardWidgetInstance, name: string, fallback: number) => {
    const value = widget.settings?.[name];
    return Number.isFinite(value) ? value : fallback;
};

const revenuePeriodSetting = (widget: DashboardWidgetInstance): RevenuePeriod => {
    const period = numberSetting(widget, "period", RevenuePeriods.Quarter);
    return period === RevenuePeriods.Week || period === RevenuePeriods.Month || period === RevenuePeriods.Year
        ? period
        : RevenuePeriods.Quarter;
};

const fullWidthOnMobile = (desktopMinimum = 1) => (columns: DashboardColumnCount) =>
    columns === 2 ? 2 : desktopMinimum;

export const dashboardWidgetRegistry: DashboardWidgetDefinition[] = [
    {
        type: "sales_analytics",
        titleKey: "dashboard.widgets.salesAnalytics.title",
        descriptionKey: "dashboard.widgets.salesAnalytics.description",
        icon: "mdi-chart-bar",
        defaultPosition: { width: 9, height: 7 },
        minimumWidth: fullWidthOnMobile(2),
        minimumHeight: 6,
        settings: [{ key: "months", labelKey: "dashboard.widgets.settings.months", minimum: 3, maximum: 24, defaultValue: 12 }],
        render: widget => <SalesAnalytics months={numberSetting(widget, "months", 12)} />,
    },
    {
        type: "active_projects",
        titleKey: "dashboard.widgets.activeProjects.title",
        descriptionKey: "dashboard.widgets.activeProjects.description",
        icon: "mdi-briefcase-outline",
        defaultPosition: { width: 9, height: 7 },
        minimumWidth: fullWidthOnMobile(2),
        minimumHeight: 5,
        settings: [{ key: "limit", labelKey: "dashboard.widgets.settings.projects", minimum: 1, maximum: 25, defaultValue: 3 }],
        render: widget => <ActiveProjectsOverview limit={numberSetting(widget, "limit", 3)} />,
    },
    {
        type: "revenue",
        titleKey: "dashboard.widgets.revenue.title",
        descriptionKey: "dashboard.widgets.revenue.description",
        icon: "mdi-cash-multiple",
        defaultPosition: { width: 3, height: 3 },
        minimumWidth: () => 1,
        minimumHeight: 3,
        settings: [{
            key: "period",
            labelKey: "dashboard.widgets.settings.period",
            minimum: RevenuePeriods.Week,
            maximum: RevenuePeriods.Year,
            defaultValue: RevenuePeriods.Quarter,
            options: [
                { value: RevenuePeriods.Year, labelKey: "dashboard.widgets.settings.thisYear" },
                { value: RevenuePeriods.Quarter, labelKey: "dashboard.widgets.settings.thisQuarter" },
                { value: RevenuePeriods.Month, labelKey: "dashboard.widgets.settings.thisMonth" },
                { value: RevenuePeriods.Week, labelKey: "dashboard.widgets.settings.thisWeek" },
            ],
        }],
        render: widget => <RevenueCard period={revenuePeriodSetting(widget)} />,
    },
    {
        type: "outstanding",
        titleKey: "dashboard.widgets.outstanding.title",
        descriptionKey: "dashboard.widgets.outstanding.description",
        icon: "mdi-clock-outline",
        defaultPosition: { width: 3, height: 4 },
        minimumWidth: () => 1,
        minimumHeight: 3,
        settings: [],
        render: () => <OutstandingCard />,
    },
    {
        type: "orphaned_payments",
        titleKey: "dashboard.widgets.orphanedPayments.title",
        descriptionKey: "dashboard.widgets.orphanedPayments.description",
        icon: "mdi-bank-transfer",
        defaultPosition: { width: 3, height: 7 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [
            { key: "limit", labelKey: "dashboard.widgets.settings.items", minimum: 1, maximum: 50, defaultValue: 10 },
            { key: "days_back", labelKey: "dashboard.widgets.settings.daysBack", minimum: 1, maximum: 365, defaultValue: 90 },
        ],
        render: widget => <OrphanedPaymentsCard
            loadData={false}
            limit={numberSetting(widget, "limit", 10)}
            daysBack={numberSetting(widget, "days_back", 90)}
        />,
    },
    {
        type: "recent_calls",
        titleKey: "dashboard.widgets.recentCalls.title",
        descriptionKey: "dashboard.widgets.recentCalls.description",
        icon: "mdi-phone-outline",
        defaultPosition: { width: 3, height: 6 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [{ key: "limit", labelKey: "dashboard.widgets.settings.items", minimum: 1, maximum: 50, defaultValue: 20 }],
        render: widget => <RecentCallsCard limit={numberSetting(widget, "limit", 20)} />,
    },
    {
        type: "asset_services",
        titleKey: "dashboard.widgets.assetServices.title",
        descriptionKey: "dashboard.widgets.assetServices.description",
        icon: "mdi-calendar-sync",
        defaultPosition: { width: 6, height: 6 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [{ key: "limit", labelKey: "dashboard.widgets.settings.items", minimum: 1, maximum: 50, defaultValue: 50 }],
        render: widget => <AssetServicesCard limit={numberSetting(widget, "limit", 50)} />,
    },
    {
        type: "upcoming_asset_services",
        titleKey: "dashboard.widgets.upcomingAssetServices.title",
        descriptionKey: "dashboard.widgets.upcomingAssetServices.description",
        icon: "mdi-calendar-clock",
        defaultPosition: { width: 6, height: 6 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [{ key: "days_ahead", labelKey: "dashboard.widgets.settings.daysAhead", minimum: 1, maximum: 365, defaultValue: 30 }],
        render: widget => <UpcomingAssetServicesCard daysAhead={numberSetting(widget, "days_ahead", 30)} />,
    },
    {
        type: "pending_hours",
        titleKey: "dashboard.widgets.pendingHours.title",
        descriptionKey: "dashboard.widgets.pendingHours.description",
        icon: "mdi-timer-sand",
        defaultPosition: { width: 6, height: 6 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [],
        render: () => <PendingHoursCard />,
    },
    {
        type: "pending_documents",
        titleKey: "dashboard.widgets.pendingDocuments.title",
        descriptionKey: "dashboard.widgets.pendingDocuments.description",
        icon: "mdi-file-check-outline",
        defaultPosition: { width: 6, height: 5 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [],
        render: () => <PendingDocumentsCard />,
    },
    {
        type: "open_invoices",
        titleKey: "dashboard.widgets.openInvoices.title",
        descriptionKey: "dashboard.widgets.openInvoices.description",
        icon: "mdi-file-clock-outline",
        defaultPosition: { width: 6, height: 6 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [],
        render: () => <OpenInvoicesCard />,
    },
    {
        type: "worker_client_balance",
        titleKey: "dashboard.widgets.workerClientBalance.title",
        descriptionKey: "dashboard.widgets.workerClientBalance.description",
        icon: "mdi-scale-balance",
        defaultPosition: { width: 12, height: 7 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 6,
        settings: [{ key: "period_days", labelKey: "dashboard.widgets.settings.periodDays", minimum: 1, maximum: 365, defaultValue: 90 }],
        render: widget => <WorkerClientBalanceCard periodDays={numberSetting(widget, "period_days", 90)} />,
    },
    {
        type: "unread_email",
        titleKey: "dashboard.widgets.unreadEmail.title",
        descriptionKey: "dashboard.widgets.unreadEmail.description",
        icon: "mdi-email-outline",
        defaultPosition: { width: 3, height: 6 },
        minimumWidth: fullWidthOnMobile(),
        minimumHeight: 4,
        settings: [{ key: "limit", labelKey: "dashboard.widgets.settings.items", minimum: 1, maximum: 50, defaultValue: 10 }],
        render: widget => <UnreadEmailCard limit={numberSetting(widget, "limit", 10)} />,
    },
];

export const dashboardWidgetByType = new Map(dashboardWidgetRegistry.map(definition => [definition.type, definition]));

export const settingsWithDefaults = (definition: DashboardWidgetDefinition, settings: Record<string, number> = {}) =>
    Object.fromEntries(definition.settings.map(field => {
        const value = settings[field.key];
        return [field.key, Number.isFinite(value) ? value : field.defaultValue];
    }));
